import { setMainValue, hasMainValue, getMainValueStr, parseBraceOrCsv } from '../core/EncodeValues.js';
import { Widget } from '../core/Widget.js';
import { ViewCmdType } from '../view/IViewWidget.js';
import { ViewWidgetHelper } from '../view/ViewWidgetHelper.js';
import { ComboBoxWidget } from './ComboBoxWidget.js';
import { DropdownWidget } from './DropdownWidget.js';

// widgets/InputCollectionWidget.ts
/**
 * Виджет коллекции однотипных полей ввода.
 *
 * Базовые возможности:
 * - Автодобавление новой строки при заполнении последней.
 * - Удаление пустых строк при очистке (с учётом data-min).
 * - Поддержка input/select/textarea и интеграция с ComboBoxWidget (.im-combo) и DropdownWidget (.dropdown).
 * - Методы API: getValue(), setValue(), destroy().
 *
 * Что НЕ входит в базовую версию:
 * - keyword mode (чипсы), text mode (единый textarea), data-suggestedmask, детальная regex-валидация.
 */
/** Базовая версия без jQuery. Наследуемся от общего Widget. */
class InputCollectionWidget extends Widget {
    constructor() {
        super(...arguments);
        /** Шаблон строки (клонируется при добавлении) */
        this.templateRow = null;
        /** Глубина батчинга (подавление onControlInput/onControlChanged) */
        this.batchLevel = 0;
        /** Нужно ли дернуть emitChanged() после окончания батча */
        this.pendingChange = false;
        /** Флаг — работаем в keyword-моде (чипсы) */
        this.isKeywords = false;
        /** Разделитель: учитывать ли точку */
        this.useDotSeparator = false;
        /** Контейнер для чипсов (.values-wrapper) */
        this.valuesWrapper = null;
        /** Флаг — работаем в text-mode (единое textarea) */
        this.isTextMode = false;
        /** Для text-mode: разделитель — перенос строки? */
        this.useNewRow = false;
        /** textarea для text-mode */
        this.textArea = null;
        /** Обработчик ввода — добавляет новую строку, если текущая стала непустой и она последняя */
        this.onControlInput = (ev) => {
            if (this.batchLevel > 0) { // программные изменения — игнорируем
                this.pendingChange = true;
                return;
            }
            // TODO: Разобраться со switcher-wrapper
            const row = ev.currentTarget
                .closest('.im-inputcollection > *:not(.switcher-wrapper)');
            if (!row)
                return;
            const isLast = this.isLastRow(row);
            const hasVal = this.controlHasValue(row);
            if (isLast && hasVal) {
                this.appendRow();
            }
        };
        /** Обработчик change — удаляет пустые строки, если их больше minCount */
        this.onControlChanged = (_ev) => {
            if (this.batchLevel > 0) {
                this.pendingChange = true;
                return;
            }
            let rows = this.rows();
            for (let i = 0; i < rows.length; i++) {
                const row = rows[i];
                const isLast = i === rows.length - 1;
                if (!isLast && !this.controlHasValue(row) && rows.length > this.minCount) {
                    row.remove();
                    rows = this.rows();
                    i = -1;
                }
            }
            this._dispatchUserChange();
            // ➜ После любого изменения в базовом режиме пересчитываем видимость подсказок
            this.resetSuggestedVisibility();
            // ➜ мягкий триггер валидации для root (без нативных change/input)
            this.element.dispatchEvent(new CustomEvent("validation:run", { bubbles: false }));
        };
        /** Зарегистрированные слушатели для корректного destroy */
        this._listeners = [];
    }
    static _cssKey() {
        return 'im-inputcollection';
    }
    beginBatch() { this.batchLevel++; }
    endBatch() {
        if (this.batchLevel > 0)
            this.batchLevel--;
        if (this.batchLevel === 0 && this.pendingChange) {
            this.pendingChange = false;
            this._dispatchUserChange();
        }
    }
    /** Жизненный цикл виджета (вызовется из init базового класса при необходимости) */
    _create(op) {
        // Помечаем что элемент использует только кастомные события change
        this.element._customChange = true;
        // --- специальные режимы ---
        this.isKeywords = !!op.keywordsmode;
        this.useDotSeparator = !!op.dotseparator;
        this.suggestedMask = op.suggestedmask;
        this.isTextMode = !!op.textmode;
        this.useNewRow = !!op.usenewrow;
        // переключатель __listmode — ищем чекбокс по ключу из опций
        this.hostKey = this.element.dataset.key;
        if (this.hostKey) {
            const scope = (this.element.closest('.im-view') ?? document);
            const listmodeCheckbox = scope.querySelector(`[data-key="${this.hostKey}__listmode"]`);
            if (listmodeCheckbox) {
                listmodeCheckbox.addEventListener('change', () => {
                    const view = ViewWidgetHelper.closestOnByName(this.element);
                    if (!view)
                        return;
                    view.reload(this.hostKey, undefined, undefined, ViewCmdType.refresh);
                });
            }
        }
        // ── TextMode
        if (this.isTextMode) {
            this.textArea = this.element.querySelector('textarea') ?? (() => {
                const ta = document.createElement('textarea');
                this.element.appendChild(ta);
                return ta;
            })();
            this.textArea.addEventListener('change', () => {
                this._dispatchUserChange();
                this.resetSuggestedVisibility();
            });
            this.textArea.addEventListener('input', () => this.resetSuggestedVisibility());
            this.initSuggestedMask();
            return;
        }
        // ── KeywordMode
        if (this.isKeywords) {
            this.initKeywordMode();
            return;
        }
        // ── Базовый режим
        this.minCount = op.min ?? 1;
        const rows = this.rows();
        if (rows.length === 0)
            return;
        this.templateRow = rows[0].cloneNode(true);
        this.beginBatch();
        try {
            this.clearRowValues(this.templateRow);
            rows.forEach((row, idx) => this.initRow(row, idx));
            while (this.rows().length < this.minCount) {
                this.appendRow();
            }
            this.pendingChange = false;
        }
        finally {
            this.endBatch();
        }
        // добавьте эту строку (инициализация кликов по подсказкам и в базовом режиме)
        this.initSuggestedMask();
    }
    /** Все строки коллекции (прямые потомки host-элемента, исключая switcher-wrapper) */
    rows() {
        return Array.from(this.element.children)
            .filter((ch) => ch instanceof HTMLElement && !ch.classList.contains('switcher-wrapper'));
    }
    /** Найти управляющий контрол для строки.
     * Поддерживает варианты:
     *  - row сам является контролом: .dropdown / .im-combo / input / select / textarea
     *  - контрол вложен внутрь row
     */
    findControl(row) {
        // --- 1) row сам — виджет/контрол ---
        if (row.classList.contains('dropdown'))
            return row; // расширенный dropdown
        if (row.classList.contains('im-combo'))
            return row; // ComboBoxWidget
        if (row.tagName === 'INPUT' && row.type !== 'checkbox')
            return row;
        if (row.tagName === 'TEXTAREA')
            return row;
        if (row.tagName === 'SELECT')
            return row;
        // --- 2) вложенный контрол внутри row ---
        const dd = row.querySelector('.dropdown');
        if (dd)
            return dd;
        const combo = row.querySelector('.im-combo');
        if (combo)
            return combo;
        const inp = row.querySelector('input:not([type="checkbox"])');
        if (inp)
            return inp;
        const sel = row.querySelector('select');
        if (sel)
            return sel;
        const ta = row.querySelector('textarea');
        if (ta)
            return ta;
        return null;
    }
    /** Сброс значения контрола внутри строки */
    clearRowValues(row) {
        const ctrl = this.findControl(row);
        if (!ctrl)
            return;
        // Унифицированная очистка через системный API
        setMainValue(ctrl, "");
    }
    /** Инициализировать строку: повесить обработчики и активировать виджеты при необходимости */
    initRow(row, _idx) {
        const ctrl = this.findControl(row);
        if (!ctrl)
            return;
        if (ctrl.classList.contains("im-combo")) {
            try {
                ComboBoxWidget.getOrInit(ctrl);
            }
            catch { }
            ctrl.addEventListener("input", this.onControlInput);
            ctrl.addEventListener("combochange", this.onControlInput);
            ctrl.addEventListener("combochange", this.onControlChanged);
            ctrl.addEventListener("change", this.onControlInput);
            ctrl.addEventListener("change", this.onControlChanged);
            return;
        }
        if (ctrl.classList.contains("dropdown")) {
            try {
                DropdownWidget.getOrInit(ctrl);
            }
            catch { }
            // оба слушателя — и на автодобавление, и на чистку пустых
            ctrl.addEventListener("ddchange", this.onControlInput);
            ctrl.addEventListener("ddchange", this.onControlChanged);
            return;
        }
        // Нативные input/select/textarea — один код
        ctrl.addEventListener("input", this.onControlInput);
        ctrl.addEventListener("change", this.onControlChanged);
    }
    /** Проверка: строка — последняя? */
    isLastRow(row) {
        const rows = this.rows();
        return rows.length > 0 && rows[rows.length - 1] === row;
    }
    /** Есть ли значение у контрола в строке */
    controlHasValue(row) {
        const ctrl = this.findControl(row);
        if (!ctrl)
            return false;
        return hasMainValue(ctrl) ?? false;
    }
    // Помощник: валидация одной строки с учётом regex
    validateRow(row, re) {
        const ctrl = this.findControl(row);
        if (!ctrl)
            return true;
        const val = getMainValueStr(ctrl)?.trim();
        const ok = !val || re.test(val);
        // Маркируем контейнер поля (родителя контрола), как в jQuery-версии
        const container = ctrl.parentElement ?? row;
        if (!val) {
            container.classList.remove('ok', 'err');
            ctrl.removeAttribute?.('title');
            ctrl.classList?.remove?.('input-validation-error');
            try {
                ctrl._tippy?.disable?.();
            }
            catch { }
        }
        else if (!ok) {
            container.classList.add('err');
            container.classList.remove('ok');
            ctrl.classList?.add?.('input-validation-error');
            if (this.element.dataset.error)
                ctrl.setAttribute?.('title', this.element.dataset.error);
        }
        else {
            container.classList.add('ok');
            container.classList.remove('err');
            ctrl.classList?.remove?.('input-validation-error');
            ctrl.removeAttribute?.('title');
            try {
                ctrl._tippy?.disable?.();
            }
            catch { }
        }
        return ok;
    }
    // Реализация контракта Validation.Validatable
    validate() {
        const pattern = this.element.dataset.regex;
        if (!pattern) {
            // снять локальные пометки, если они были
            if (!this.isKeywords && !this.isTextMode) {
                for (const row of this.rows()) {
                    row.classList.remove('ok', 'err');
                }
            }
            return { ok: true };
        }
        let re;
        try {
            re = new RegExp(pattern);
        }
        catch {
            return { ok: true };
        } // не валим UX, если regex сломан
        // keyword/text modes — валидируем значения getValue()
        if (this.isKeywords || this.isTextMode) {
            const vals = this.getValue();
            const ok = vals.every(v => !v || re.test(v));
            return ok ? { ok } : { ok, message: this.element.dataset.error };
        }
        // base mode — ещё и размечаем строки
        let okAll = true;
        for (const row of this.rows()) {
            okAll = this.validateRow(row, re) && okAll;
        }
        return okAll ? { ok: true } : { ok: false, message: this.element.dataset.error };
    }
    /** Добавить новую строку на основе шаблона, активировать виджеты и повесить обработчики */
    appendRow() {
        if (!this.templateRow)
            return;
        const clone = this.templateRow.cloneNode(true);
        // если нужен data-parentkey — переносим с корня
        const ctrl = this.findControl(clone);
        if (ctrl && this.hostKey) {
            ctrl.setAttribute("data-parentkey", this.hostKey);
        }
        this.element.appendChild(clone);
        this.initRow(clone, this.rows().length - 1);
        this.clearRowValues(clone);
    }
    /** Сбор значений */
    getValue() {
        if (this.isKeywords) {
            const res = [];
            const wrap = this.valuesWrapper;
            if (!wrap)
                return res;
            wrap.querySelectorAll('.item-value').forEach(it => {
                const v = it.getAttribute('data-value') ?? '';
                if (v)
                    res.push(v);
            });
            return res;
        }
        if (this.isTextMode) {
            const ta = this.textArea;
            if (!ta)
                return [];
            const src = ta.value ?? '';
            const rx = this.useNewRow ? /[\n]/ : /[\n,]/;
            return src
                .split(rx)
                .map(s => s.trim())
                .filter(Boolean);
        }
        // базовый режим (строки коллекции)
        const parts = [];
        for (const row of this.rows()) {
            const ctrl = this.findControl(row);
            if (!ctrl)
                continue;
            const v = getMainValueStr(ctrl);
            if (v)
                parts.push(v);
        }
        return parts;
    }
    /** Установка значений */
    setValue(val) {
        const values = parseBraceOrCsv(val);
        if (this.isKeywords) {
            // чипсы: очищаем и пересоздаём
            if (!this.valuesWrapper) {
                this.valuesWrapper = document.createElement('div');
                this.valuesWrapper.className = 'values-wrapper';
                this.element.appendChild(this.valuesWrapper);
            }
            // удалить все чипсы
            this.valuesWrapper.querySelectorAll('.item-value').forEach(el => el.remove());
            // добавить новые
            for (const v of values)
                this.appendChip(v, v, true);
            this.resetSuggestedVisibility();
            this.element.dispatchEvent(new CustomEvent("validation:run", { bubbles: false }));
            return true;
        }
        if (this.isTextMode) {
            const ta = this.textArea ?? this.element.querySelector('textarea');
            if (!ta)
                return false;
            ta.value = this.useNewRow
                ? values.join('\n')
                : values.join(', ');
            // синхронизируем подсказки, событие change не триггерим программно
            this.resetSuggestedVisibility();
            this.element.dispatchEvent(new CustomEvent("validation:run", { bubbles: false }));
            return true;
        }
        // базовый режим (строки коллекции)
        this.beginBatch();
        try {
            // очистка и обеспечение минимума
            for (const row of this.rows())
                row.remove();
            while (this.rows().length < Math.max(this.minCount, 1)) {
                this.appendRow();
            }
            // заполнение
            for (const v of values) {
                const rows = this.rows();
                const last = rows[rows.length - 1];
                this.setControlValue(last, v);
                this.appendRow(); // пустая строка в конец
            }
        }
        finally {
            this.pendingChange = false;
            this.endBatch();
        }
        this.resetSuggestedVisibility();
        this.element.dispatchEvent(new CustomEvent("validation:run", { bubbles: false }));
        return true;
    }
    /**
     * true — если виджет считает, что значение задано
     * - в keywordsmode: есть хотя бы один чипс (.item-value)
     * - в базовом режиме: есть хотя бы одна непустая строка (controlHasValue)
     */
    hasValue() {
        if (this.isKeywords) {
            // есть контейнер и в нём хотя бы один чипс
            return !!this.valuesWrapper && !!this.valuesWrapper.querySelector('.item-value');
        }
        if (this.isTextMode) {
            return this.getValue().length > 0;
        }
        // базовый режим: пробегаем строки и ищем первую с непустым контролом
        for (const row of this.rows()) {
            if (this.controlHasValue(row))
                return true;
        }
        return false;
    }
    /** Присвоить значение контролу в строке */
    setControlValue(row, value) {
        const ctrl = this.findControl(row);
        if (!ctrl) {
            console.error('[InputCollectionWidget] Контрол не найден в строке', row, 'при попытке присвоить значение', value);
            return;
        }
        // Установка основного значения
        setMainValue(ctrl, value);
    }
    /** Инициализация keywordsmode (режим чипсов) */
    initKeywordMode() {
        // 1) Находим/создаем контейнер для чипсов
        this.valuesWrapper = this.element.querySelector('.values-wrapper');
        if (!this.valuesWrapper) {
            this.valuesWrapper = document.createElement('div');
            this.valuesWrapper.className = 'values-wrapper';
            this.element.appendChild(this.valuesWrapper);
        }
        // 2) Делегированный клик по чипсам для удаления
        this.on(this.element, 'click', (e) => {
            const t = e.target;
            if (t && t.classList.contains('item-value')) {
                this.removeChip(t);
            }
        });
        // 3) Источник ввода (combobox / dropdown / текстовый input/select/textarea)
        //    Используем общий поиск первого подходящего контрола.
        //    При желании можно дать разметке хинт через [data-role="keywords-input"].
        const preferred = this.element.querySelector('[data-role="keywords-input"]');
        const ctrl = preferred ??
            this.element.querySelector('.im-combo, .dropdown, input:not([type=checkbox]), select, textarea');
        if (!ctrl) {
            this.initSuggestedMask(); // на всякий
            return;
        }
        // 3.1) combobox
        if (ctrl.classList.contains('im-combo')) {
            try {
                ComboBoxWidget.getOrInit(ctrl);
            }
            catch { /* noop */ }
            this.on(ctrl, 'combochange', () => this.beforeAppendKeyword(ctrl, 'combobox'));
            this.on(ctrl, 'change', () => this.beforeAppendKeyword(ctrl, 'combobox'));
            this.on(ctrl, 'keyup', (ev) => {
                const e = ev;
                if (e.key === 'Enter' || e.keyCode === 13) {
                    this.beforeAppendKeyword(ctrl, 'combobox');
                    ctrl.classList.remove('ui-autocomplete-loading'); // как раньше
                }
            });
            this.on(ctrl, 'autocompleteclose', () => {
                const mode = (ctrl.getAttribute('data-mode') || '').toLowerCase();
                if (mode !== 'freetext')
                    ctrl.value = '';
            });
        }
        // 3.2) dropdown
        else if (ctrl.classList.contains('dropdown')) {
            try {
                DropdownWidget.getOrInit(ctrl);
            }
            catch { /* noop */ }
            this.on(ctrl, 'ddchange', () => this.beforeAppendKeyword(ctrl, 'DropdownAdaptiveElement'));
        }
        else {
            // 3.3) textbox/select/textarea → используем change
            this.on(ctrl, 'change', () => {
                this.beforeAppendKeyword(ctrl, 'textbox');
                if (ctrl instanceof HTMLInputElement || ctrl instanceof HTMLTextAreaElement) {
                    ctrl.value = '';
                }
            });
        }
        // Предотвращаем уход из поля ввода при нажатии tab
        this.on(ctrl, 'keydown', (ev) => {
            const e = ev;
            if (e.key === 'Enter' || e.key === 'Tab') {
                e.preventDefault(); // не уходим с фокуса
                this.beforeAppendKeyword(ctrl, 'combobox'); // или textbox, если нужно
            }
        });
        // suggestedMask общий для keywordsmode, инициализируем независимо от типа ctrl
        this.initSuggestedMask();
    }
    /**
     * Подготовить и добавить значения в keywordsmode.
     * @param ctrl Источник (combobox/dropdown/input/select/textarea)
     * @param elemType Тип источника, влияет на логику извлечения значения/текста
     */
    beforeAppendKeyword(ctrl, elemType) {
        switch (elemType) {
            case 'DropdownAdaptiveElement': {
                const v = ctrl.getAttribute('data-value') ?? '';
                const t = ctrl.getAttribute('data-text') ?? v;
                if (v) {
                    this.appendChip(v, t);
                }
                break;
            }
            case 'combobox': {
                const mode = (ctrl.getAttribute('data-mode') || '').toLowerCase(); // 'freetext'?
                const dv = ctrl.getAttribute('data-value') ?? '';
                const visible = ctrl.value ?? '';
                const title = ctrl.getAttribute('data-title') ?? '';
                const text = title || visible;
                if (mode === 'freetext') {
                    // freetext: если нет data-value — берём введённый текст; парсим по разделителям
                    const raw = dv || text || '';
                    this.parseTextAndAppend(raw);
                    ctrl.setAttribute('data-value', '');
                    ctrl.value = '';
                }
                else {
                    const v = dv || visible;
                    const t = text || v;
                    if (t)
                        this.appendChip(v, t);
                    ctrl.value = '';
                }
                break;
            }
            default: { // textbox/select/textarea
                const el = ctrl;
                if (el instanceof HTMLSelectElement) {
                    const opt = el.selectedOptions?.[0];
                    const val = opt?.value ?? el.value ?? '';
                    const text = (opt?.text ?? '').trim() || val;
                    if (val)
                        this.appendChip(val, text);
                    // при необходимости можно сбрасывать выбор:
                    // el.selectedIndex = -1;
                }
                else {
                    const raw = el.value ?? '';
                    this.parseTextAndAppend(raw);
                    el.value = '';
                }
                break;
            }
        }
    }
    /** Разобрать строку по разделителям и добавить чипсы (с нормализацией) */
    parseTextAndAppend(txt) {
        const raw = (txt || '').replace(/\s{2,}/g, ' ').replace(/\.{2,}/g, '.');
        if (!raw)
            return;
        const rx = this.useDotSeparator ? /[,\.;]/ : /[,;]/;
        for (let part of raw.split(rx)) {
            const t = part.trim();
            if (!t || t.length <= 1)
                continue;
            // капитализация первой буквы (поведение старой версии)
            const first = t.charAt(0).toUpperCase();
            const val = first + t.slice(1);
            this.appendChip(val, val);
        }
    }
    /** Добавить чипс, без дублей по data-value */
    /** Добавить чипс, без дублей по data-value (кейсы игнорируем) */
    appendChip(value, text, silent = false) {
        if (!this.valuesWrapper)
            return;
        const v = String(value ?? '').trim();
        if (!v)
            return;
        const key = v.toLowerCase();
        // проверка дублей: по data-value и по тексту (регистронезависимо)
        const exists = Array.from(this.valuesWrapper.querySelectorAll('.item-value'))
            .some(it => {
            const dv = (it.getAttribute('data-value') ?? '').trim().toLowerCase();
            const tt = (it.textContent ?? '').trim().toLowerCase();
            return dv === key || tt === key;
        });
        if (exists)
            return;
        const span = document.createElement('span');
        span.className = 'item-value appeared';
        span.setAttribute('data-value', v);
        span.textContent = text ?? v;
        this.valuesWrapper.appendChild(span);
        window.setTimeout(() => span.classList.remove('appeared'), 1600);
        if (!silent)
            this._dispatchUserChange();
        this.resetSuggestedVisibility();
        this.element.dispatchEvent(new CustomEvent("validation:run", { bubbles: false }));
    }
    /** Удалить чипс по DOM-элементу */
    removeChip(itemEl) {
        itemEl.remove();
        this._dispatchUserChange();
        this.resetSuggestedVisibility();
        this.element.dispatchEvent(new CustomEvent("validation:run", { bubbles: false }));
    }
    /** Унифицированная подписка с автотрекингом для destroy (без generic) */
    on(el, type, fn) {
        el.addEventListener(type, fn);
        this._listeners.push({ el, type, fn });
    }
    /** Снятие всех слушателей */
    offAll() {
        for (const { el, type, fn } of this._listeners) {
            el.removeEventListener(type, fn);
        }
        this._listeners = [];
    }
    // ↓ новый метод: инициализация кликов по подсказкам
    initSuggestedMask() {
        if (!this.suggestedMask)
            return;
        // 1) зона поиска подсказок: ближайшая .im-view, иначе весь документ
        const scope = (this.element.closest('.im-view') ?? document);
        // 2) делегированный клик по подсказкам
        this.on(scope, 'click', (e) => {
            const target = e.target;
            if (!target || !target.matches?.(this.suggestedMask))
                return;
            const keyword = (target.textContent ?? '').trim();
            if (!keyword)
                return;
            if (this.isKeywords) {
                this.appendChip(keyword, keyword);
            }
            else if (this.isTextMode) {
                // вставляем слово в textarea с учётом разделителя
                const ta = this.textArea ?? this.element.querySelector('textarea');
                if (ta) {
                    const cur = (ta.value ?? '').trim();
                    if (!cur) {
                        ta.value = keyword;
                    }
                    else {
                        ta.value = this.useNewRow ? (cur + '\n' + keyword) : (cur + ', ' + keyword);
                    }
                    // обновляем подсказки и эмитим change для внешней логики
                    this.resetSuggestedVisibility();
                    ta.dispatchEvent(new Event('change', { bubbles: true }));
                }
            }
            else {
                // базовый режим: пишем в последний контрол
                const rows = this.rows();
                const last = rows.length ? rows[rows.length - 1] : undefined;
                if (last) {
                    const ctrl = this.findControl(last);
                    if (ctrl) {
                        setMainValue(ctrl, keyword);
                        ctrl.dispatchEvent?.(new Event('input', { bubbles: true }));
                        ctrl.dispatchEvent?.(new Event('change', { bubbles: true }));
                    }
                }
            }
            target.style.display = 'none';
        });
        // начальная синхронизация видимости
        this.resetSuggestedVisibility();
    }
    // ↓ новый метод: скрыть подсказки, которые уже присутствуют среди чипсов
    resetSuggestedVisibility() {
        if (!this.suggestedMask)
            return;
        const scope = (this.element.closest('.im-view') ?? document);
        const norm = (s) => s.trim().toLowerCase();
        const selected = new Set(this.isKeywords
            ? Array.from(this.valuesWrapper?.querySelectorAll('.item-value') ?? [])
                .map(it => norm(it.textContent ?? ''))
                .filter(Boolean)
            : this.getValue().map(v => norm(String(v))));
        scope.querySelectorAll(this.suggestedMask).forEach(el => {
            const text = norm(el.textContent ?? '');
            if (!text)
                return;
            el.style.display = selected.has(text) ? 'none' : '';
        });
    }
    /** Очистка слушателей и состояний (идемпотентно) */
    _onDestroy() {
        // снять все слушатели, зарегистрированные через this.on(...)
        this.offAll();
        // базовые (построчные) контролы
        for (const row of this.rows()) {
            const ctrl = this.findControl(row);
            if (!ctrl)
                continue;
            ctrl.replaceWith(ctrl.cloneNode(true)); // быстро снимаем слушатели на клоне
        }
        // keywordsmode: чисто для порядка
        this.valuesWrapper = null;
    }
}
/** Имя виджета */
InputCollectionWidget._alias = "InputCollection";

export { InputCollectionWidget };
